// The portal of a 3D ad: conversion between the API shape and the editor's state, the request payload, and the UI-only rules.
// A 2D ad's portal is just its click link (see buildAdClickPayload).
//
// API shape (booth portal shape, type 11):
//   { id, localizedPairs: { key, values }, correspondingMedia: { id, mediaTypeId, linkLocalizations },
//     portalLinks: [ { id, positionId, externalLink, thumbnailMedia: { id, mediaTypeId, linkLocalizations } } ] }
//
// Editor state (positions[i] is positionId i, always 4 entries):
//   { id, titleKey, title: [{ localeId, value }], mainMediaId, mainMediaTypeId, mainMediaLinks: [{ localeId, mediaLink }]  (as stored; NOT edited:
//     a saved portal's main media is the preview image of the selected 3D model, see resolveAdMainMedia),
//     positions: [ { linkId, portalLink, thumbnailMediaId, thumbnailLocalizations: [{ localeId, mediaLink }] } ] }
//
// Portal rules are UI-only (like the booth): the server enforces only the model rules and that ids belong to the ad's portal.
import { AD_PORTAL_POSITIONS } from '../constants/portalPositions';
import { emptyPosition, positionHasContent, positionIsComplete, positionIsPartial } from '../components/portals/PortalShared';
import { hasRequiredName, isHttpsUrl, isValidUrl } from './validators';

// portal_link.external_link and media_localization.media_link are varchar(255)
export const URL_MAX_LENGTH = 255;

const blankLink = () => [{ localeId: 'en_us', mediaLink: '' }];
const blankTitle = () => [{ localeId: 'en_us', value: '' }];
const clean = (s) => (s ?? '').trim();

export const emptyAdPosition = () => ({ ...emptyPosition(), linkId: null, thumbnailMediaId: null });

export const emptyAdPortal = () => ({
  id: null,
  titleKey: null,
  title: blankTitle(),
  mainMediaId: null,
  mainMediaTypeId: 1,
  mainMediaLinks: blankLink(),
  positions: AD_PORTAL_POSITIONS.map(emptyAdPosition),
});

const cloneLinks = (links) =>
  Array.isArray(links) && links.length > 0
    ? links.map((l) => ({ localeId: l.localeId, mediaLink: l.mediaLink ?? '' }))
    : blankLink();

/**
 * The editor state for an API portal (an ad that has one) or an empty one.
 * keepIds=false is "Duplicate as new ad": the copy must create NEW rows, so the portal, link and media ids and the title key are dropped.
 */
export const adPortalFromApi = (portal, { keepIds = true } = {}) => {
  if (!portal) return emptyAdPortal();
  const idOf = (id) => (keepIds && id > 0 ? id : null);

  return {
    id: idOf(portal.id),
    titleKey: keepIds ? (portal.localizedPairs?.key ?? null) : null,
    title: portal.localizedPairs?.values?.length > 0
      ? portal.localizedPairs.values.map((v) => ({ localeId: v.localeId, value: v.value ?? '' }))
      : blankTitle(),
    mainMediaId: idOf(portal.correspondingMedia?.id),
    mainMediaTypeId: portal.correspondingMedia?.mediaTypeId > 0 ? portal.correspondingMedia.mediaTypeId : 1,
    mainMediaLinks: cloneLinks(portal.correspondingMedia?.linkLocalizations),
    positions: AD_PORTAL_POSITIONS.map(({ id: positionId }) => {
      const link = (portal.portalLinks ?? []).find((l) => l.positionId === positionId);
      if (!link) return emptyAdPosition();
      return {
        linkId: idOf(link.id),
        portalLink: link.externalLink ?? '',
        thumbnailMediaId: idOf(link.thumbnailMedia?.id),
        thumbnailLocalizations: cloneLinks(link.thumbnailMedia?.linkLocalizations),
      };
    }),
  };
};

const nonEmptyLinks = (links) =>
  (links ?? []).filter((l) => clean(l.mediaLink)).map((l) => ({ localeId: l.localeId, mediaLink: clean(l.mediaLink) }));

/**
 * The main media of an ad's portal is the preview image of the selected 3D model: nobody types it. Its media type and localized URLs
 * come from the model's `previewMedia`; null when the model has no usable preview image.
 */
export const mainMediaFromModel = (model) => {
  const links = nonEmptyLinks(model?.previewMedia?.linkLocalizations);
  if (links.length === 0) return null;
  return { mediaTypeId: model.previewMedia.mediaTypeId > 0 ? model.previewMedia.mediaTypeId : 1, links };
};

/**
 * The main media a saved portal gets. The model is known: its preview image. The model is not known here (the registry list could not
 * be loaded): the main media the portal already has is kept. null = there is none (the model has no preview image), so the portal
 * cannot be saved.
 */
export const resolveAdMainMedia = (portal, model) => {
  if (model) return mainMediaFromModel(model);
  const kept = nonEmptyLinks(portal?.mainMediaLinks);
  return kept.length > 0 ? { mediaTypeId: portal.mainMediaTypeId > 0 ? portal.mainMediaTypeId : 1, links: kept } : null;
};

/** True when the portal has at least one COMPLETE position (link + thumbnail): the only thing that makes a portal worth saving. */
export const adPortalHasLinks = (portal) => Boolean(portal?.positions?.some(positionIsComplete));

/** True when anything at all was typed into the portal. */
export const adPortalHasContent = (portal) =>
  Boolean(
    portal && (
      portal.title?.some((t) => clean(t.value)) ||
      portal.positions?.some(positionHasContent)
    ),
  );

/**
 * The `portal` of a create / update request.
 *  - complete positions only (empty and partial ones are not sent; the UI blocks partial ones);
 *  - ids go back as loaded (a new link / thumbnail has none, a duplicate has none at all);
 *  - no complete position: `{ portalLinks: [] }` when the ad HAD a portal (it is removed), otherwise null (nothing to send).
 * `loadedPortal` is the portal of the STORED ad (a rejected proposal's snapshot may already say "removed"), null for a new ad or a duplicate.
 * `mainMedia` is { mediaTypeId, links } from resolveAdMainMedia (the model's preview image); without it the portal's own main media is sent.
 * Only the media TYPE and the localized URLs come from the model: the media row id is always the portal's own (a foreign id is error 52113).
 */
export const buildAdPortalPayload = (portal, loadedPortal = null, mainMedia = null) => {
  const portalLinks = (portal?.positions ?? [])
    .map((position, positionId) => ({ position, positionId }))
    .filter(({ position }) => positionIsComplete(position))
    .map(({ position, positionId }) => {
      const link = {
        positionId,
        externalLink: clean(position.portalLink),
        thumbnailMedia: { mediaTypeId: 1, linkLocalizations: nonEmptyLinks(position.thumbnailLocalizations) },
      };
      if (position.linkId > 0) link.id = position.linkId;
      if (position.thumbnailMediaId > 0) link.thumbnailMedia.id = position.thumbnailMediaId;
      return link;
    });

  if (portalLinks.length === 0) return loadedPortal ? { portalLinks: [] } : null;

  const main = mainMedia ?? { mediaTypeId: portal.mainMediaTypeId > 0 ? portal.mainMediaTypeId : 1, links: nonEmptyLinks(portal.mainMediaLinks) };
  const payload = {
    localizedPairs: {
      values: (portal.title ?? []).filter((t) => clean(t.value)).map((t) => ({ localeId: t.localeId, value: t.value.trim() })),
    },
    correspondingMedia: { mediaTypeId: main.mediaTypeId, linkLocalizations: main.links },
    portalLinks,
  };
  if (portal.titleKey) payload.localizedPairs.key = portal.titleKey;
  if (portal.id > 0) payload.id = portal.id;
  if (portal.mainMediaId > 0) payload.correspondingMedia.id = portal.mainMediaId;
  return payload;
};

// The click link of a 2D ad: its portal has exactly one link, at the Center position (id 1), no thumbnail, and a title that is the
// ad's name plus this suffix (nobody types it; the API checks it).
export const CLICK_LINK_POSITION_ID = 1;
export const CLICK_LINK_TITLE_SUFFIX = ' - Portal';

// "<ad name> - Portal" for every locale the ad name has a non-blank value in.
const clickLinkTitle = (adName) => (adName ?? [])
  .filter((n) => n?.localeId && clean(n.value))
  .map((n) => ({ localeId: n.localeId, value: `${clean(n.value)}${CLICK_LINK_TITLE_SUFFIX}` }));

const clickLinkOf = (portal) => {
  const links = portal?.portalLinks ?? [];
  return links.find((l) => l.positionId === CLICK_LINK_POSITION_ID) ?? links[0] ?? null;
};

/** The URL of a 2D ad's click link, from the portal the API returned ('' when there is none). */
export const clickLinkFromPortal = (portal) => clickLinkOf(portal)?.externalLink ?? '';

/**
 * The `portal` of a 2D ad's request: the click link, with the ad's own media type and URLs copied into the portal's main media
 * (its own row: the portal's main media is never the ad's media row).
 *  - blank URL: `{ portalLinks: [] }` when the STORED ad had a portal (it is removed), otherwise null (nothing to send);
 *  - `localizedPairs.values` is "<ad name> - Portal" per locale of the ad name (so it also replaces a leftover 3D title); no `key`
 *    is sent: the server uses the portal's own;
 *  - the portal and main media ids go back as loaded; the link id only when the stored ad is 2D, so a 3D -> 2D switch inserts a new
 *    link and the old 3D links and thumbnails are replaced, not reused.
 * `adMedia` is { mediaTypeId, links } of the ad being saved (1 image / 2 video, its localized URLs); `adName` is its name values
 * [{ localeId, value }].
 */
export const buildAdClickPayload = (url, loadedPortal = null, adMedia = null, { loadedIsTwoD = false, adName = [] } = {}) => {
  const externalLink = clean(url);
  if (!externalLink) return loadedPortal ? { portalLinks: [] } : null;

  const payload = {
    localizedPairs: { values: clickLinkTitle(adName) },
    correspondingMedia: {
      mediaTypeId: adMedia?.mediaTypeId > 0 ? adMedia.mediaTypeId : 1,
      linkLocalizations: nonEmptyLinks(adMedia?.links),
    },
    portalLinks: [{ positionId: CLICK_LINK_POSITION_ID, externalLink }],
  };
  if (loadedPortal?.id > 0) payload.id = loadedPortal.id;
  if (loadedPortal?.correspondingMedia?.id > 0) payload.correspondingMedia.id = loadedPortal.correspondingMedia.id;
  const loadedLinkId = clickLinkOf(loadedPortal)?.id;
  if (loadedIsTwoD && loadedLinkId > 0) payload.portalLinks[0].id = loadedLinkId;
  return payload;
};

const tooLong = (value) => clean(value).length > URL_MAX_LENGTH;

/**
 * Everything the form can tell without asking the network (the reachability checks are added by the editor).
 *   partial        position ids with only a link or only a thumbnail (invalid until completed or cleared)
 *   badLinks       position ids whose link is not a valid https:// URL
 *   badThumbnails  position ids whose thumbnail URL is not a valid http(s) URL
 *   longPositions  position ids with a link or thumbnail URL longer than 255 characters
 *   tooLong        a position URL is longer than 255 characters
 *   missingTitle   required once any position is complete (the main media is not asked: it is the model's preview image)
 */
export const adPortalIssues = (portal) => {
  const positions = portal?.positions ?? [];
  const idsWhere = (test) => positions.map((p, i) => (test(p) ? i : -1)).filter((i) => i >= 0);

  const hasLinks = adPortalHasLinks(portal);
  const longPositions = idsWhere((p) => tooLong(p.portalLink) || (p.thumbnailLocalizations ?? []).some((t) => tooLong(t.mediaLink)));
  const issues = {
    hasContent: adPortalHasContent(portal),
    hasLinks,
    partial: idsWhere(positionIsPartial),
    badLinks: idsWhere((p) => clean(p.portalLink) && !isHttpsUrl(p.portalLink)),
    badThumbnails: idsWhere((p) => (p.thumbnailLocalizations ?? []).some((t) => clean(t.mediaLink) && !isValidUrl(t.mediaLink))),
    longPositions,
    tooLong: longPositions.length > 0,
    missingTitle: hasLinks && !hasRequiredName(portal?.title),
  };
  issues.ok =
    issues.partial.length === 0 && issues.badLinks.length === 0 && issues.badThumbnails.length === 0 &&
    !issues.tooLong && !issues.missingTitle;
  return issues;
};

/** An empty portal is valid (it is optional); a started one must satisfy every rule. */
export const isAdPortalValid = (portal) => adPortalIssues(portal).ok;
